import {
  useChartStyles,
  coreBlackTooltipConfig,
  coreChartConfig,
  coreLinearScaleOptions,
  coreTimeScaleOptions,
  weekendIndicationOnAxisPlugin
} from '@hconnect/common-chartjs-config'
import {dataTestId, dateFormatter, dayNameFormatter, formatTons} from '@hconnect/uikit'
import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {Box, lighten, useTheme, Skeleton} from '@mui/material'
import {Chart as ChartJS} from 'chart.js/auto'
import type {ChartOptions, ChartData, LegendItem, ChartEvent} from 'chart.js/auto'
import 'chartjs-adapter-moment'
import annotationPlugin, {
  type AnnotationOptions,
  type BoxAnnotationOptions
} from 'chartjs-plugin-annotation'
import {isEmpty} from 'lodash'
import moment, {Moment} from 'moment-timezone'
import {useCallback, useEffect, useMemo} from 'react'
import {Chart} from 'react-chartjs-2'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useElementWidth, useFilter} from '../hooks'
import {useMaterialDemandFilters} from '../providers'
import {type CombinedDemandData} from '../selectors'
import {
  type MomentRange,
  convertToDate,
  getListOfDays,
  getLocalizedShortMonthDayFormat
} from '../utils'

ChartJS.register(annotationPlugin)

const DEMAND_FORECAST_CHART_HEIGHT = 250

interface MaterialDemandChartProps {
  demandData: CombinedDemandData | undefined
  timeFrame: MomentRange
  timezoneId: string
}

export const MaterialDemandChart: React.FC<MaterialDemandChartProps> = ({
  timeFrame: [start, end],
  timezoneId,
  demandData
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {palette} = useTheme()
  const {
    tickColor,
    gridLineColor: gridColor,
    weekendBackground,
    selectedBackground,
    hoverBackground
  } = useChartStyles()
  const [ref, width] = useElementWidth()

  const {merged, actuals, preOrders, salesForecast} = demandData || {}

  const {selectedDate, toggleSelectedDate} = useMaterialDemandFilters()

  const isSelectedDay = useCallback(
    (day: Moment) => (!selectedDate ? false : convertToDate(day.tz(timezoneId)) === selectedDate),
    [selectedDate, timezoneId]
  )

  const listOfDays = useMemo(
    () => getListOfDays({start, end}).map((date) => date.clone().tz(timezoneId)),
    [timezoneId, start, end]
  )

  const labels = useMemo(() => listOfDays.map((entry) => convertToDate(entry)), [listOfDays])

  const {
    values: hiddenDatasets,
    toggleValue: toggleHiddenDataset,
    addValue: hideDataset
  } = useFilter<string>()

  const annotations = useMemo<AnnotationOptions[]>(
    () =>
      listOfDays.map((entry) => ({
        type: 'box' as const,
        drawTime: 'beforeDatasetsDraw',
        xMin: entry.clone().startOf('day').subtract(12, 'hours').valueOf(),
        xMax: entry.clone().endOf('day').subtract(12, 'hours').valueOf(),
        backgroundColor: isSelectedDay(entry) ? selectedBackground : 'transparent',
        borderWidth: 0,
        enter(context) {
          if (isSelectedDay(entry)) return
          const options = context.element.options as BoxAnnotationOptions
          options.backgroundColor = hoverBackground
        },
        leave(context) {
          if (isSelectedDay(entry)) return
          const options = context.element.options as BoxAnnotationOptions
          options.backgroundColor = 'transparent'
        },
        click(context, event) {
          // ignoring clicks outside of the annotation box
          if (event.y === null || event.y < context.element.y || event.y > context.element.y2)
            return
          toggleSelectedDate(convertToDate(entry))
        }
      })),
    [listOfDays, hoverBackground, isSelectedDay, toggleSelectedDate, selectedBackground]
  )

  const options = useMemo<ChartOptions>(() => {
    const handleLegendClick = (e: ChartEvent, legendItem: LegendItem) => {
      toggleHiddenDataset(legendItem.text)
    }

    return {
      ...coreChartConfig,
      onHover: function (event, activeElements) {
        if (activeElements.length && event.native?.target) {
          const target = event.native.target as HTMLCanvasElement
          target.style.cursor = 'pointer'
        }
      },
      scales: {
        x: {
          ...coreTimeScaleOptions,
          stacked: true,
          ticks: {
            ...coreTimeScaleOptions.ticks,
            source: 'labels',
            autoSkip: false,
            callback(value) {
              const epochTime = Number(value)
              const date = moment.utc(epochTime).tz(timezoneId)
              if (listOfDays.length <= 31) {
                return getLocalizedShortMonthDayFormat(date, language)
              }
              if (listOfDays.length <= 31 * 3) {
                return date.day() === 1
                  ? getLocalizedShortMonthDayFormat(date, language)
                  : undefined
              }
              return date.date() === 1 ? getLocalizedShortMonthDayFormat(date, language) : undefined
            },
            color: tickColor
          },
          grid: {...coreTimeScaleOptions.grid, display: false}
        },
        y: {
          ...coreLinearScaleOptions,
          stacked: false,
          grid: {
            ...coreLinearScaleOptions.grid,
            color: gridColor
          },
          ticks: {
            ...coreLinearScaleOptions.ticks,
            callback(value, index) {
              const lastTickIndex = this.ticks.length - 1
              if (index === lastTickIndex) return t('common.unit.t')
              return value
            },
            color: tickColor
          }
        }
      },
      plugins: {
        tooltip: {
          ...coreBlackTooltipConfig,
          titleFont: coreBlackTooltipConfig.bodyFont,
          bodyFont: coreBlackTooltipConfig.titleFont,
          callbacks: {
            title: function (context) {
              const dateUNIX = context[0].parsed.x
              const date = moment.utc(dateUNIX).tz(timezoneId)
              return `${dayNameFormatter(date, language)}, ${dateFormatter(date, language)}`
            },
            label: function ({parsed, chart}) {
              const dateUNIX = parsed.x
              const date = convertToDate(moment.utc(dateUNIX).tz(timezoneId))
              return chart.data.datasets
                .map((dataset) => {
                  const label = dataset.label
                  const value: number | undefined = (
                    dataset.data as unknown as Record<string, number>
                  )[date]
                  if (value === undefined) return '' // this value is falsy so it would be filtered out by the filter(Boolean) below
                  return `${label}:\t${formatTons(value, language)}`
                })
                .filter(Boolean)
            }
          }
        },
        legend: {
          display: true,
          align: 'end',
          onClick: handleLegendClick,
          labels: {
            color: tickColor,
            useBorderRadius: true,
            borderRadius: 6,
            boxWidth: 12,
            boxHeight: 12
          }
        },
        weekendIndicationOnAxisPlugin: {
          backgroundColor: weekendBackground,
          axisName: 'x',
          isShifted: true
        },
        annotation: {annotations}
      }
    }
  }, [
    annotations,
    t,
    language,
    timezoneId,
    listOfDays,
    toggleHiddenDataset,
    gridColor,
    tickColor,
    weekendBackground
  ])

  const data = useMemo<ChartData<'bar', Record<string, number>>>(() => {
    const sharedBarConfig = {
      pointRadius: 0,
      borderWidth: 0,
      type: 'bar' as const
    }

    const datasets = [
      ...(isEmpty(preOrders)
        ? []
        : [
            {
              label: t('demand.demandLegend.preOrder'),
              data: preOrders,
              backgroundColor: 'rgba(0, 0, 0, 0)',
              borderColor: customThemeConstants().palette.accent1,
              borderWidth: 2,
              stack: 'forecastAndPreorder'
            }
          ]),
      {
        label: t('demand.demandLegend.statisticalForecast'),
        data: merged ?? {},
        backgroundColor: lighten(customThemeConstants().palette.accent2, 0.3),
        stack: 'forecastAndPreorder'
      },
      ...(isEmpty(salesForecast)
        ? []
        : [
            {
              label: t('demand.demandLegend.salesForecast'),
              data: salesForecast,
              backgroundColor: customThemeConstants().palette.accent3,
              stack: 'salesForecast'
            }
          ]),
      {
        label: t('demand.demandLegend.actualSales'),
        data: actuals ?? {},
        backgroundColor: palette.primary.light,
        stack: 'sales'
      }
    ].map((dataset) => ({
      ...sharedBarConfig,
      ...dataset,
      hidden: hiddenDatasets.includes(dataset.label)
    }))

    return {labels, datasets}
  }, [labels, merged, actuals, preOrders, salesForecast, t, palette, hiddenDatasets])

  // this callbacks removes dataset from hidden if it disappears from the chart
  //  so that at the time it appears again it is not hidden
  useEffect(() => {
    if (!demandData) return
    hiddenDatasets.forEach((hiddenDataset) => {
      if (!data.datasets?.find((dataset) => dataset.label === hiddenDataset)) {
        hideDataset(hiddenDataset)
      }
    })
  }, [data.datasets, hiddenDatasets, hideDataset, demandData])

  if (!demandData) {
    return <Skeleton variant="rectangular" height={DEMAND_FORECAST_CHART_HEIGHT} />
  }

  const chartKey = `demand-chart-${DEMAND_FORECAST_CHART_HEIGHT}-${width}`
  return (
    <Box
      ref={ref}
      {...dataTestId(`material_demand_chart_${start.toISOString()}_${end.toISOString()}`)}
    >
      <Chart
        key={chartKey}
        type="bar"
        options={options}
        data={data}
        width={width}
        height={DEMAND_FORECAST_CHART_HEIGHT}
        plugins={[weekendIndicationOnAxisPlugin]}
      />
    </Box>
  )
}
